import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useApolloClient} from '@apollo/react-hooks';

import type {MarketId, ProductId} from 'nutshell-core/types';

import type {
    SearchProducts as SearchProductsQuery,
    SearchProductsVariables,
} from 'nutshell-graphql-types';

import {toProductPickerProduct} from '../helpers/product-picker-product';
import type {ProductPickerProduct} from '../helpers/product-picker-product';
import SEARCH_PRODUCTS from '../queries/search-products.graphql';

export const MIN_PRODUCT_SEARCH_LENGTH = 2;
const PRODUCT_SEARCH_DEBOUNCE_MS = 250;
const MAX_OMITTED_PRODUCT_IDS = 500;

type ProductSearchOptions = {
    marketId?: MarketId;
    omittedProductIds?: ProductId[];
};

type ProductSearchResult = ProductPickerProduct[] | null;

type PendingSearch = {
    timeout: ReturnType<typeof setTimeout>;
    resolve: (products: ProductSearchResult) => void;
};

type ActiveSearch = {
    sequence: number;
    resolve: (products: ProductSearchResult) => void;
};

export class ProductSearchExclusionLimitError extends Error {
    constructor() {
        super('Remove a selected product before searching for another.');
        this.name = 'ProductSearchExclusionLimitError';
    }
}

export function useSearchProducts({marketId, omittedProductIds = []}: ProductSearchOptions): {
    searchProducts: (query: string) => Promise<ProductSearchResult>;
    error: unknown | null;
} {
    const client = useApolloClient();
    const requestSequence = useRef<number>(0);
    const pendingSearch = useRef<PendingSearch | null>(null);
    const activeSearch = useRef<ActiveSearch | null>(null);
    const [error, setError] = useState<unknown | null>(null);
    const omittedProductIdsKey = JSON.stringify([...omittedProductIds].sort());
    const stableOmittedProductIds = useMemo(
        () => JSON.parse(omittedProductIdsKey) as ProductId[],
        [omittedProductIdsKey]
    );

    const invalidateSearch = useCallback(() => {
        requestSequence.current += 1;
        if (pendingSearch.current) {
            clearTimeout(pendingSearch.current.timeout);
            pendingSearch.current.resolve(null);
            pendingSearch.current = null;
        }
        if (activeSearch.current) {
            activeSearch.current.resolve(null);
            activeSearch.current = null;
        }
    }, []);

    useEffect(() => {
        invalidateSearch();
        setError(null);
    }, [invalidateSearch, marketId, omittedProductIdsKey]);

    useEffect(() => invalidateSearch, [invalidateSearch]);

    const searchProducts = useCallback(
        (rawQuery: string): Promise<ProductSearchResult> => {
            invalidateSearch();
            setError(null);

            const query = rawQuery.trim();
            if (query.length < MIN_PRODUCT_SEARCH_LENGTH) return Promise.resolve([]);

            if (stableOmittedProductIds.length > MAX_OMITTED_PRODUCT_IDS) {
                setError(new ProductSearchExclusionLimitError());
                return Promise.resolve(null);
            }

            const sequence = requestSequence.current;
            return new Promise<ProductSearchResult>((resolve) => {
                const timeout = setTimeout(() => {
                    pendingSearch.current = null;
                    activeSearch.current = {sequence, resolve};
                    client
                        .query<SearchProductsQuery, SearchProductsVariables>({
                            query: SEARCH_PRODUCTS,
                            variables: {
                                search: query,
                                ...(marketId ? {marketId} : {}),
                                ...(stableOmittedProductIds.length
                                    ? {omittedIds: stableOmittedProductIds}
                                    : {}),
                            },
                            fetchPolicy: 'no-cache',
                        })
                        .then(({data}) => {
                            if (sequence !== requestSequence.current) {
                                resolve(null);
                                return;
                            }

                            resolve(
                                data.productsSearch.map((result) =>
                                    toProductPickerProduct(result, marketId)
                                )
                            );
                        })
                        .catch((searchError: unknown) => {
                            if (sequence === requestSequence.current) setError(searchError);
                            resolve(null);
                        })
                        .finally(() => {
                            if (activeSearch.current?.sequence === sequence) {
                                activeSearch.current = null;
                            }
                        });
                }, PRODUCT_SEARCH_DEBOUNCE_MS);

                pendingSearch.current = {timeout, resolve};
            });
        },
        [client, invalidateSearch, marketId, stableOmittedProductIds]
    );

    return {searchProducts, error};
}
